import L10n from "localization";
import { PiClockCountdown } from "react-icons/pi";
import Style from "style";
import { FSRSDueStatus, getFSRSDueStatus } from "./fsrsDueStatus";

/**
 * Props for the FSRSDue component
 */
interface IProps {
  /** Unix timestamp in milliseconds when the card is due */
  dueTimestamp: number;
  /** Optional font size for the due status display */
  fontSize?: string | number;
}

/**
 * Gets the background color that should be used for the FSRS due status
 * @param dueTimestamp Unix timestamp in milliseconds when the card is due
 * @returns Color string to use as background color
 */
export function getFSRSDueColor(dueTimestamp: number): string {
  const status = getFSRSDueStatus(dueTimestamp);
  switch (status) {
    case "Now":
      return Style.colors.grades.F; // Most urgent - red color for due/overdue
    case "Within24hr":
      return Style.colors.grades.D; // Urgent - orange color
    case "Tomorrow":
      return Style.colors.grades.C; // Important - yellow color
    case "Within1wk":
      return Style.colors.grades.B; // Moderate - light blue color
    case "Future":
      return Style.colors.grades.A; // Least urgent - green color
  }
}

/**
 * Displays FSRS card due status with a clock icon and localized text
 * Shows "Now", "Today", "Tomorrow", "This Week", or "Future" based on the due timestamp
 *
 * @param props Component props containing due timestamp and optional styling
 * @returns JSX element with clock icon and due status text
 */
export default function FSRSDue(props: IProps): JSX.Element {
  try {
    const { dueTimestamp, fontSize = undefined } = props;

    // Validate timestamp
    if (!Number.isFinite(dueTimestamp)) {
      console.warn("Invalid dueTimestamp provided to FSRSDue:", dueTimestamp);
      return <span>{L10n.localize((s) => s.fsrs.dueTitle) || "Due"}</span>;
    }

    const status = getFSRSDueStatus(dueTimestamp);

    /**
     * Gets localized text for the due status with fallbacks
     */
    const getStatusText = (status: FSRSDueStatus): string => {
      try {
        switch (status) {
          case "Now":
            return L10n.localize((s) => s.general.now);
          case "Within24hr":
            return L10n.localize((s) => s.fsrs.dueWithin24hr);
          case "Tomorrow":
            return L10n.localize((s) => s.general.tomorrow) || "Tomorrow";
          case "Within1wk":
            return L10n.localize((s) => s.fsrs.dueWithin1wk) || "<1wk";
          case "Future":
            return L10n.localize((s) => s.general.future) || "Future";
          default:
            return L10n.localize((s) => s.fsrs.dueTitle) || "Due";
        }
      } catch (error) {
        console.warn("Failed to localize FSRS due status:", error);
        // Return English fallback
        return status;
      }
    };

    const style: React.CSSProperties = {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      fontSize: fontSize,
      textTransform: "uppercase",
    };

    return (
      <span style={style}>
        <PiClockCountdown />
        <span>{getStatusText(status)}</span>
      </span>
    );
  } catch (error) {
    console.error("Fatal error in FSRSDue component:", error);
    // Minimal fallback to prevent crash
    return <span>{L10n.localize((s) => s.fsrs.dueTitle) || "Due"}</span>;
  }
}
